import { SearchCustomerBy4TResp } from '@ytl/common-model';
import { useTheme } from '@ytl/common-web';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { OnboardingMethod } from '../../shared/@types/onboardingMethods';
import { DAPCode } from '../../shared/components/partials/onboarding/DAPCode';
import { DAPVerify } from '../../shared/components/partials/onboarding/DAPVerify';
import { ErrorPage } from '../../shared/components/partials/onboarding/ErrorPage';
import { Finish } from '../../shared/components/partials/onboarding/Finish';
import { HasEmail } from '../../shared/components/partials/onboarding/HasEmail';
import { IdentifyMyself } from '../../shared/components/partials/onboarding/IdentifyMyself';
import { KYCApp } from '../../shared/components/partials/onboarding/KYCApp';
import { KYCDataChecker } from '../../shared/components/partials/onboarding/KYCDataChecker';
import { KYCWeb } from '../../shared/components/partials/onboarding/KYCWeb';
import { SelectSubscriber } from '../../shared/components/partials/onboarding/SelectSubscriber';
import { Verification } from '../../shared/components/partials/onboarding/Verification';
import { Banned } from '../../shared/components/partials/verification/Banned';

export enum OnboardingPhase {
  KYCDataChecker = 'KYCDataChecker',
  IdentifyMyself = 'identifyMyself',
  KYCWeb = 'KYCWeb',
  DAPCode = 'dapCode',
  VideoChat = 'videoChat',
  Appointment = 'appointment',
  DAPVerify = 'dapVerify',
  SelectSubscriber = 'selectSubscriber',
  HasEmail = 'hasEmail',
  Verification = 'verification',
  Finish = 'finish',
  KYCError = 'kycError', // Not part of main flow, called manually on errors
  KYCInit = 'kycInit', // Not part of main flow, called manually on errors
  Error = 'error', // Not part of main flow, called manually on errors
  Banned = 'banned', // Not part of main flow, called manually
}

export enum OnboardingPath {
  Default = 'default',
  DAPVerification = 'dapVerification',
  VideoChat = 'videoChat',
  Appointment = 'appointment',
  HasEmail = 'hasEmail',
}

// JWT payload structure for init params (normalized to camelCase)
export type InitParamsJWTPayload = {
  clientID?: string;
  sessionID?: string;
  withPassword?: boolean;
  captchaNeeded?: boolean;
  captchaSiteKey?: string;
  captchaInvisible?: boolean;
  emailVerification?: boolean;
  tmsVerification?: boolean;
  msisdnVerification?: boolean;
  createDUP?: boolean;
  salesMsisdn?: string;
  salesCA?: string;
  ipBanningEnabled?: boolean;
  language?: 'hu' | 'en';
  theme?: 'dark' | 'light';
  additionalInfo?: Record<string, unknown>;
};

// Parsed init params with defaults applied
export type InitParams = {
  clientID: string;
  sessionID: string;
  withPassword: boolean;
  captchaNeeded: boolean;
  captchaSiteKey?: string;
  captchaInvisible: boolean;
  emailVerification: boolean;
  tmsVerification: boolean;
  msisdnVerification: boolean;
  createDUP: boolean;
  salesMsisdn?: string;
  salesCA?: string;
  ipBanningEnabled: boolean;
  language: 'hu' | 'en';
  theme: 'dark' | 'light';
  additionalInfo: Record<string, unknown>;
};

type StepConfig = {
  phase: OnboardingPhase;
  skip?: boolean;
  requiredPaths?: OnboardingPath[];
};

type StepperConfig = {
  steps: StepConfig[];
  initialStep?: number;
  initialPaths?: OnboardingPath[];
};

export type OnboardingStepperControls = {
  currentStep: number;
  currentPhase: OnboardingPhase;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  activePaths: Set<OnboardingPath>;
  goToNext: () => void;
  goToPrevious: () => void;
  goToStep: (step: number) => void;
  goToPhase: (phase: OnboardingPhase) => void;
  addPath: (path: OnboardingPath) => void;
  removePath: (path: OnboardingPath) => void;
  setPath: (path: OnboardingPath, active: boolean) => void;
  hasPath: (path: OnboardingPath) => boolean;
  showError: (errorCode?: string, hideIcon?: boolean) => void;
  showBanned: () => void;
  resetOnboarding: (resetStateCallback?: () => void) => void;
  errorCode?: string;
  errorHideIcon: boolean;
};

const useStepper = (config: StepperConfig): OnboardingStepperControls => {
  const [currentStep, setCurrentStep] = useState(config.initialStep ?? 0);
  const [activePaths, setActivePaths] = useState<Set<OnboardingPath>>(
    () => new Set(config.initialPaths ?? [OnboardingPath.Default]),
  );
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
  const [errorHideIcon, setErrorHideIcon] = useState(false);

  // Sync activePaths when initialPaths changes (e.g., when initParams becomes available)
  useEffect(() => {
    if (config.initialPaths) {
      setActivePaths(new Set(config.initialPaths));
    }
  }, [config.initialPaths]);

  const activeSteps = useMemo(() => {
    return config.steps.filter(step => {
      if (step.skip) {
        return false;
      }

      if (step.requiredPaths && step.requiredPaths.length > 0) {
        return step.requiredPaths.some(path => activePaths.has(path));
      }

      return true;
    });
  }, [config.steps, activePaths]);

  const totalSteps = activeSteps.length;

  const currentPhase = useMemo(
    () => activeSteps[currentStep]?.phase ?? OnboardingPhase.IdentifyMyself,
    [activeSteps, currentStep],
  );

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const goToNext = useCallback(() => {
    if (!isLastStep) {
      setCurrentStep(prev => prev + 1);
    }
  }, [isLastStep]);

  const goToPrevious = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isFirstStep]);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setCurrentStep(step);
      }
    },
    [totalSteps],
  );

  const goToPhase = useCallback(
    (phase: OnboardingPhase) => {
      const stepIndex = activeSteps.findIndex(step => step.phase === phase);
      if (stepIndex !== -1) {
        setCurrentStep(stepIndex);
      }
    },
    [activeSteps],
  );

  const addPath = useCallback((path: OnboardingPath) => {
    setActivePaths(prev => {
      const next = new Set(Array.from(prev));
      next.add(path);
      return next;
    });
  }, []);

  const removePath = useCallback((path: OnboardingPath) => {
    setActivePaths(prev => {
      const next = new Set(Array.from(prev));
      next.delete(path);
      return next;
    });
  }, []);

  const setPath = useCallback(
    (path: OnboardingPath, active: boolean) => {
      if (active) {
        addPath(path);
      } else {
        removePath(path);
      }
    },
    [addPath, removePath],
  );

  const hasPath = useCallback(
    (path: OnboardingPath) => {
      return activePaths.has(path);
    },
    [activePaths],
  );

  const showError = useCallback(
    (code?: string, hideIcon = false) => {
      setErrorCode(code);
      setErrorHideIcon(hideIcon);
      goToPhase(OnboardingPhase.Error);
    },
    [goToPhase],
  );

  const showBanned = useCallback(() => {
    goToPhase(OnboardingPhase.Banned);
  }, [goToPhase]);

  const resetOnboarding = useCallback((resetStateCallback?: () => void) => {
    setErrorCode(undefined);
    setErrorHideIcon(false);
    setCurrentStep(0);
    setActivePaths(new Set([OnboardingPath.Default]));
    if (resetStateCallback) {
      resetStateCallback();
    }
  }, []);

  return {
    currentStep,
    currentPhase,
    totalSteps,
    isFirstStep,
    isLastStep,
    activePaths,
    goToNext,
    goToPrevious,
    goToStep,
    goToPhase,
    addPath,
    removePath,
    setPath,
    hasPath,
    showError,
    showBanned,
    resetOnboarding,
    errorCode,
    errorHideIcon,
  };
};

type OnboardingComponentProps = {
  method?: OnboardingMethod;
};

const OnboardingComponentInner = ({ method = OnboardingMethod.Default }: OnboardingComponentProps) => {
  const { isWebView } = useTheme();

  const [, setSelectedSubscriber] = useState<{
    id: string;
    name: string;
    phoneNumber: string;
  } | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<string | null>(null);
  const [selectedCustomerData, setSelectedCustomerData] = useState<SearchCustomerBy4TResp | undefined>(undefined);

  useLayoutEffect(() => {
    if (isWebView) {
      document.documentElement.classList.add('ytl--app');
    }
    return () => {
      document.documentElement.classList.remove('ytl--app');
    };
  }, [isWebView]);

  // Build initial paths based on initParams
  const initialPaths = useMemo(() => {
    const paths: OnboardingPath[] = [OnboardingPath.Default];
    return paths;
  }, []);

  // Function to reset all onboarding state
  const resetAllState = useCallback(() => {
    setSelectedSubscriber(null);
    setEmail(null);
    setSessionId(null);
    setCustomerData(null);
    setSelectedCustomerData(undefined);
  }, []);

  const getSteps = useCallback(() => {
    if (method === OnboardingMethod.KYC) {
      return [
        { phase: OnboardingPhase.KYCDataChecker },
        { phase: OnboardingPhase.SelectSubscriber },
        { phase: OnboardingPhase.HasEmail, requiredPaths: [OnboardingPath.HasEmail] },
        { phase: OnboardingPhase.Verification },
        { phase: OnboardingPhase.Finish },
        { phase: OnboardingPhase.KYCError }, // Not part of normal flow, accessed via showError
        { phase: OnboardingPhase.Error }, // Not part of normal flow, accessed via showError
        { phase: OnboardingPhase.Banned }, // Not part of normal flow, accessed via showBanned
      ];
    }
    if (method === OnboardingMethod.KYCError) {
      return [
        { phase: OnboardingPhase.KYCError }, // Not part of normal flow, accessed via showError
      ];
    }
    if (method === OnboardingMethod.KYCInit) {
      return [
        { phase: OnboardingPhase.KYCInit }, // Not part of normal flow, accessed via showError
      ];
    }
    //default
    return [
      { phase: OnboardingPhase.IdentifyMyself },
      { phase: OnboardingPhase.DAPCode, requiredPaths: [OnboardingPath.DAPVerification] },
      { phase: OnboardingPhase.DAPVerify },
      { phase: OnboardingPhase.SelectSubscriber },
      { phase: OnboardingPhase.HasEmail, requiredPaths: [OnboardingPath.HasEmail] },
      { phase: OnboardingPhase.Verification },
      { phase: OnboardingPhase.Finish },
      { phase: OnboardingPhase.KYCWeb }, // Not part of normal flow, accessed via showError
      { phase: OnboardingPhase.Error }, // Not part of normal flow, accessed via showError
      { phase: OnboardingPhase.Banned }, // Not part of normal flow, accessed via showBanned
    ];
  }, [method]);

  const stepperConfig: StepperConfig = useMemo(
    () => ({
      steps: getSteps(),
      initialStep: 0,
      initialPaths,
    }),
    [initialPaths, getSteps],
  );

  const stepper = useStepper(stepperConfig);

  // Inject resetAllState into stepper's resetOnboarding
  useEffect(() => {
    const originalReset = stepper.resetOnboarding;
    stepper.resetOnboarding = callback => {
      originalReset(() => {
        resetAllState();
        callback?.();
      });
    };
  }, [stepper, resetAllState]);

  useEffect(() => {}, [isWebView]);

  const renderStep = () => {
    switch (stepper.currentPhase) {
      case OnboardingPhase.KYCDataChecker:
        return (
          <KYCDataChecker
            stepper={stepper}
            setCustomerData={setCustomerData}
          />
        );
      case OnboardingPhase.IdentifyMyself:
        return <IdentifyMyself stepper={stepper} />;
      case OnboardingPhase.KYCWeb:
        return <KYCWeb stepper={stepper} />;
      case OnboardingPhase.DAPCode:
        return (
          <DAPCode
            stepper={stepper}
            sessionId={sessionId}
            setSessionId={setSessionId}
          />
        );
      case OnboardingPhase.DAPVerify:
        return (
          <DAPVerify
            stepper={stepper}
            sessionId={sessionId}
            setCustomerData={setCustomerData}
          />
        );
      case OnboardingPhase.SelectSubscriber:
        return (
          <SelectSubscriber
            stepper={stepper}
            customerData={customerData}
            method={method}
            onContinue={subscriberData => {
              setSelectedSubscriber(subscriberData);
            }}
            setEmail={newEmail => {
              setEmail(newEmail);
              if (newEmail) {
                stepper.addPath(OnboardingPath.HasEmail);
              } else {
                stepper.removePath(OnboardingPath.HasEmail);
              }
            }}
            setSelectedCustomerData={setSelectedCustomerData}
          />
        );
      case OnboardingPhase.HasEmail:
        return (
          <HasEmail
            stepper={stepper}
            email={email}
          />
        );
      case OnboardingPhase.Verification:
        return (
          <Verification
            stepper={stepper}
            email={email}
            selectedSubscriber={selectedCustomerData}
            customerDataJWT={customerData}
            tmsVerification={true}
          />
        );
      case OnboardingPhase.Finish:
        return <Finish />;
      case OnboardingPhase.Error:
        return (
          <ErrorPage
            stepper={stepper}
            errorCode={stepper.errorCode}
            hideIcon={stepper.errorHideIcon}
          />
        );
      case OnboardingPhase.KYCError:
        return (
          <ErrorPage
            stepper={stepper}
            errorCode={'KYC_ERROR'}
            hideIcon
            initiatedFrom={'KYC'}
          />
        );
      case OnboardingPhase.KYCInit:
        return <KYCApp />;
      case OnboardingPhase.Banned:
        return <Banned />;
      default:
        return null;
    }
  };

  return <>{renderStep()}</>;
};

export const OnboardingComponent = ({ method = OnboardingMethod.Default }: OnboardingComponentProps) => {
  return <OnboardingComponentInner method={method} />;
};
